'use client';

import { useTranslations } from 'next-intl';
import { StoreLink } from '@/components/store';
import type { Category, NavigationItem } from '@cobrastyle/shared-types';
import { cn } from '@cobrastyle/ui';
import { brandImage } from '@/lib/brands';
import type { CategoryPromoSlot } from '@/lib/category-promo-slots';
import { PromoTile } from './promo-tile';

export interface BrandMenu {
  /** The curated main brands, already selected and ordered by the server. */
  brands: Category[];
  /** How many brands exist in total, for the "view all N" label. */
  total: number;
}

interface MegaMenuProps {
  /** The hovered top-level nav item. Guaranteed to have children by the caller. */
  item: NavigationItem;
  /** Non-null only for the brands category, which has its own logo-grid layout. */
  menuBrands?: BrandMenu | null;
  /** Merchant-authored promo tiles for this category, if any. */
  promoSlots?: CategoryPromoSlot[];
}

// The design's hairlines are percentages of the ink colour, not the grey
// `--color-border` the rest of the chrome rules with: on the panel's near-white
// ground a tinted grey at 10% all but disappears. Kept as named constants
// because the two weights are a deliberate hierarchy — the column rule reads
// first, then the link list.
const RULE_COLUMN = 'border-foreground/10';
const RULE_LINK = 'border-foreground/[0.06]';

/**
 * The dropdown panel under a top-level nav item: the brands logo grid for the
 * brands category, and the stacked list for everything else — whether or not
 * the catalogue has a third level under it, so no two panels can drift apart.
 */
export function MegaMenu({ item, menuBrands, promoSlots }: MegaMenuProps) {
  return (
    // The accent-tinted bottom edge is the design's, in place of the grey
    // hairline the rest of the chrome uses — it closes the panel off as a
    // surface of its own rather than as one more ruled band in the header.
    <div className="absolute inset-x-0 top-full z-50 border-b border-accent-600/35 bg-overlay shadow-xl">
      <div className="container mx-auto pb-11 pt-[30px]">
        {/* Panel title — one big heading for the whole panel rather than
            repeating the category name in the left column too. */}
        <h2 className="mb-5 font-heading text-2xl font-semibold text-heading lg:text-3xl">
          {item.name}
        </h2>
        {menuBrands ? (
          <BrandsLayout menuBrands={menuBrands} item={item} promoSlots={promoSlots} />
        ) : (
          <StackedLayout groups={item.children} promoSlots={promoSlots} />
        )}
      </div>
    </div>
  );
}

// One row of the list, shared by a parent and by its children so the two can
// never drift apart. `-mx-2 px-2` lets the hover wash bleed past the text
// without moving the text itself, keeping every name flush with the panel
// heading above it.
const ROW_BASE =
  'block truncate -mx-2 rounded px-2 transition-colors duration-base ease-out hover:bg-accent-50/60 hover:text-accent-foreground';

/**
 * Every child as one ruled row, with its own children — when it has any —
 * underneath it in a smaller, quieter hand. This is the only layout a
 * non-brands category gets, whether or not the catalogue has a third level
 * under it, so column widths, rule lengths and hover behaviour are identical
 * from one category to the next.
 *
 * The children are not indented. An indent reads as a nested list of the same
 * kind of thing, which is wrong here — these are a different kind of thing, and
 * saying so twice (indent *and* a different hand) made the parent look like a
 * heading. Size, colour and tighter leading carry it instead, with the group's
 * single rule closing off the parent and its children together as one item.
 *
 * CSS columns rather than a grid: the blocks are uneven (on this catalogue one
 * child has seven children and the next has none) and columns balance that,
 * where grid tracks would leave one long column beside three near-empty ones.
 * `break-inside-avoid` keeps a parent with its children.
 */
function StackedLayout({
  groups,
  promoSlots,
}: {
  groups: NavigationItem[];
  promoSlots?: CategoryPromoSlot[];
}) {
  const hasPromos = Boolean(promoSlots?.length);

  return (
    <div
      className={cn(
        'grid items-start gap-x-11 gap-y-8',
        hasPromos && 'md:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_250px_250px]',
      )}
    >
      {/* A ternary, not two conditional classes: tailwind-merge has no rule for
          `columns-*`, so `lg:columns-4` and `lg:columns-2` both survived a merge
          and stylesheet order picked the wrong one — which is why a category
          with promo tiles used to lay itself out in four narrow columns, one of
          them empty, while every other panel used two wide ones. */}
      {/* The panel itself only exists from `lg`, and at that width the content
          column is ~930px — which the promo pair takes 588px of. Four tracks
          there (or two beside promos) leaves ~150px a name, so every second
          category truncates. Each arrangement therefore holds one step back
          until `xl`, where there is room for it. */}
      <div
        className={cn('gap-x-11', hasPromos ? 'lg:columns-1 xl:columns-2' : 'lg:columns-2 xl:columns-4')}
      >
        {groups.map((group) => (
          <div key={group.id} className={cn('break-inside-avoid border-b', RULE_LINK)}>
            <StoreLink
              href={`/${group.urlKey}`}
              className={cn(ROW_BASE, 'py-[8.5px] text-sm leading-[1.4] text-foreground/75')}
            >
              {group.name}
            </StoreLink>
            {group.children.length > 0 && (
              // Tucked tight under their parent and loose before the next one,
              // so the gaps group them without a rule or an indent doing it.
              <div className="flex flex-col pb-2">
                {group.children.map((child) => (
                  <StoreLink
                    key={child.id}
                    href={`/${child.urlKey}`}
                    className={cn(ROW_BASE, 'py-[3px] text-[12.5px] leading-[1.5] text-muted')}
                  >
                    {child.name}
                  </StoreLink>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <PromoTiles slots={promoSlots} />
    </div>
  );
}

/**
 * The brands category, whose 70-odd children are a curated logo grid rather than
 * a name list. The only layout that keeps a "view all" link: the eight shown are
 * a deliberate selection, not everything there is, so without it the rest of the
 * catalogue's brands are unreachable from the menu.
 */
function BrandsLayout({
  menuBrands,
  item,
  promoSlots,
}: {
  menuBrands: BrandMenu;
  item: NavigationItem;
  promoSlots?: CategoryPromoSlot[];
}) {
  const tBrands = useTranslations('brands');
  const hasPromos = Boolean(promoSlots?.length);

  return (
    <div
      className={cn(
        'grid items-start gap-x-11 gap-y-8 md:grid-cols-2 lg:grid-cols-4',
        hasPromos && 'lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_250px_250px]',
      )}
    >
      <div className={cn('lg:col-span-1 lg:border-r lg:pr-11', RULE_COLUMN)}>
        <StoreLink
          href={`/${item.urlKey}`}
          className="text-xs font-semibold uppercase tracking-[0.04em] text-accent-foreground transition-colors duration-base ease-out hover:text-accent-800"
        >
          {tBrands('viewAllCount', { count: menuBrands.total })}
          {' →'}
        </StoreLink>
      </div>

      <div className={cn('lg:col-span-3', hasPromos && 'lg:col-span-1')}>
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
          {menuBrands.brands.map((brand) => {
            const image = brandImage(brand.image);
            return (
              <StoreLink
                key={brand.id}
                href={`/${brand.urlKey}`}
                // Centred and sized for a logo, matching the cards on the brands
                // page: a left-aligned mark leaves a wide tile looking
                // half-empty, and a logo tile beside a wordmark tile should read
                // as the same kind of thing.
                className="group flex min-h-[4rem] items-center justify-center rounded-lg border border-border bg-background px-3 py-3 text-center text-sm transition-colors duration-base ease-out hover:border-accent-600/50 hover:bg-accent-50/60"
              >
                {image ? (
                  // Plain <img> for the same reason as MainBrands: a bad
                  // merchant-uploaded host must not 500 the header on every page
                  // of the site.
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={image}
                    alt={brand.name}
                    loading="lazy"
                    // `mix-blend-multiply` for the same reason as the brands page
                    // (main-brands.tsx, which explains it in full) and every
                    // product image on the site: several of these logos are JPEGs
                    // or flattened PNGs with a white box baked into the artwork,
                    // and without this that box sits on the tile as a bright
                    // rectangle — Nocpix and Northunt both did. Multiply drops the
                    // white onto whatever is behind it, which is the tile's own
                    // #f0f0ef, and on hover the accent tint it turns.
                    className="max-h-10 w-auto max-w-full object-contain mix-blend-multiply"
                  />
                ) : (
                  <span className="truncate text-base font-semibold text-foreground transition-colors duration-base ease-out group-hover:text-accent-foreground">
                    {brand.name}
                  </span>
                )}
              </StoreLink>
            );
          })}
        </div>
      </div>

      <PromoTiles slots={promoSlots} />
    </div>
  );
}

function PromoTiles({ slots }: { slots?: CategoryPromoSlot[] }) {
  if (!slots?.length) return null;
  return (
    <div className="grid grid-cols-2 gap-4 md:col-span-2 lg:col-span-2 lg:grid-cols-[250px_250px]">
      {slots.map((slot) => (
        <PromoTile key={slot.linkUrl} slot={slot} />
      ))}
    </div>
  );
}
